/* eslint-disable max-statements */
import { useCallback, useEffect, useState } from 'react';
import { Box } from '@nimbus-ds/components';
import { CogIcon } from '@nimbus-ds/icons';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Country, Domain } from '@tiendanube/common/src/enums';
import { DownloadIcon, PlusCircleIcon } from '@tiendanube/icons';
import { UpsellResultEnum } from '@tiendanube/nexo';
import EmailValidationAlert from 'App/components/EmailValidationAlert';
import StockIssuesAlert from 'App/components/StockIssuesAlert';
import { FEATURE_SEARCH_FILTER } from 'App/features';
import {
  ORDERS_AUTO_CANCEL_PROMOTION,
  ORDERS_LIST_PAGE_FULL_WIDTH,
} from 'App/featuresFlags';
import { useHasTags, useNavegate, useQuery } from 'App/hooks';
import { useResponsive } from 'commons/components';
import {
  IonPageStratus,
  ActionProp,
  ActionsDesktop,
  HeaderContent,
} from 'commons/components/IonPageStratus';
import { useControlCta } from 'commons/hooks/useControlCta';
import useModal from 'commons/hooks/useModal';
import {
  ORDERS_SAVED_SEARCH_FILTER,
  ORDERS_STOCK_ISSUES_FILTER,
} from 'config/upsellFlowSources';
import { useGetCountry, useHasPermission } from 'domains/Auth/hooks';
import { useUpsellFlow } from 'domains/Billing/UpsellFlow/hooks';
import {
  BulkActionsProvider,
  useBulkActions,
} from 'domains/Catalog/Products/pages/ProductListPage/components/BulkActionsProvider';
import {
  AlertOrderMetafields,
  ErrorMetafieldAlert,
} from 'domains/Metafields/components';
import NuvemEnvioWelcomeCreditCommunication from 'domains/NuvemEnvioCommons/components/WelcomeCreditCommunication';
import HeaderOrderMobile from 'domains/Orders/components/HeaderOrderMobile';
import { CalloutCardActivateNuvemPago } from 'domains/Orders/components/Nudges/CalloutCardActivateNuvemPago';
import { CtaActivateNuvemPago } from 'domains/Orders/components/Nudges/CtaActivateNuvemPago/CtaActivateNuvemPago';
import { useSavedSearches } from 'domains/Orders/components/SavedSearches';
import { METAFIELD_FILTER_PREFIX } from 'domains/Orders/constants';
import {
  trackingOrderListAutoCancelButtonClick,
  trackingOrderListCreateDraftOrderClick,
  trackingOrderListExportClick,
  trackingOrdersSearchFilterClick,
} from 'domains/Orders/tracking';
import { useAppsLinks } from 'domains/PartnersApps/hooks';
import {
  useGetNuvemPagoFromProviderList,
  useInstalledPaymentProviders,
} from 'domains/Payments/PaymentProviders/hooks';
import ExtraOptionsHeaderDesktop from './components/ExtraOptionsHeaderDesktop/ExtraOptionsHeaderDesktop';
import { OrderListEmpty } from './components/OrderListEmpty';
import OrderListFilter from './components/OrderListFilter';
import OrderListResults from './components/OrderListResults';
import { OrdersAutoCancelPromotionModal } from './components/OrdersAutoCancelPromotionalModal';
import { isNuvemEnvioLink } from '../../../../NuvemEnvioCommons/Orders/utils';
import { useOrdersList, useTotalOrders } from '../../hooks';
import {
  useIsOrdersAutoCancelEnabledForUpsell,
  useIsOrdersAutoCancelEnabled,
} from '../../hooks/useIsOrdersAutoCancelEnabled';
import { useOrdersAutoCancelUpsell } from '../../hooks/useOrdersAutoCancelUpsell';
import {
  defaultFilterParams,
  defaultFilters,
  FiltersParamsType,
} from '../../ordersService';
import ChargebackAlert from '../OrderDetailsPage/components/ChargebackAlert';

function OrderListPageWithProvider(): JSX.Element {
  return (
    <BulkActionsProvider>
      <OrdersListPage />
    </BulkActionsProvider>
  );
}

function OrdersListPage(): JSX.Element {
  const { t } = useTranslation([Domain.ORDERS]);
  const { isDesktop } = useResponsive();
  const { goTo } = useNavegate();
  const { search } = useLocation();
  const { hasOrder, isLoadingHasOrders } = useTotalOrders();
  const { componentType, isCtaVisible, isCalloutVisible } = useControlCta();
  const {
    isLoading,
    statusSuccess,
    ordersIds,
    ordersCount,
    filters,
    totalOpenOrders,
    changeFilters,
    getOrdersList,
    removeMetafieldsFilters,
    hasStockIssues,
    fetchStockIssuesCount,
  } = useOrdersList();
  const { fetchSavedSearchesCount } = useSavedSearches();
  const query = useQuery();
  const storeCountry = useGetCountry();

  const isOrdersAutoCancelEnabled = useIsOrdersAutoCancelEnabled();
  const isOrdersAutoCancelEnabledForUpsell =
    useIsOrdersAutoCancelEnabledForUpsell();
  const { selectedRowsId, showAlertMetafields } = useBulkActions();
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [showModal, onOpenModal, onCloseModal] = useModal();
  const { paymentProviders, fetchInstalledPaymentProviders } =
    useInstalledPaymentProviders();
  const { nuvemPagoIsActive } =
    useGetNuvemPagoFromProviderList(paymentProviders);

  const { getAppsLinks } = useAppsLinks();
  const { isMobile } = useResponsive();
  const [hasAutoCancelPromotionTag] = useHasTags(ORDERS_AUTO_CANCEL_PROMOTION);
  const [hasFullWidthOrdersListPageTag] = useHasTags(
    ORDERS_LIST_PAGE_FULL_WIDTH,
  );

  const knownFilterKeys = Object.keys(defaultFilters) as Array<
    keyof FiltersParamsType
  >;

  const metafieldPattern = new RegExp(
    `^${METAFIELD_FILTER_PREFIX}[a-f\\d]{8}(-[a-f\\d]{4}){4}[a-f\\d]{8}$`,
    'i',
  );

  const hasPrintShippingLabelPermission = useHasPermission(
    'print_order_shipping_labels',
  );

  const isMetafieldParameter = (key: string): boolean =>
    metafieldPattern.test(key);

  const getFilters = (keys: IterableIterator<string>) =>
    Array.from(keys).reduce((filters, key) => {
      const value = query.get(key);

      if (
        value &&
        (knownFilterKeys.includes(key as keyof FiltersParamsType) ||
          isMetafieldParameter(key))
      ) {
        // We parse the page to match the expected type
        return { ...filters, [key]: key === 'page' ? Number(value) : value };
      }
      return filters;
    }, {});

  // Only fetch payment providers if autoCancel is enabled and providers aren't loaded yet
  useEffect(() => {
    if (isOrdersAutoCancelEnabled && !paymentProviders) {
      fetchInstalledPaymentProviders();
    }
  }, [
    isOrdersAutoCancelEnabled,
    paymentProviders,
    fetchInstalledPaymentProviders,
  ]);

  useEffect(() => {
    const queryParam = query.keys();

    const filters = {
      ...defaultFilterParams,
      ...getFilters(queryParam),
    } as FiltersParamsType;

    getOrdersList(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  useEffect(() => {
    if (statusSuccess === 'errorMetafield') {
      removeMetafieldsFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusSuccess]);

  useEffect(() => {
    fetchSavedSearchesCount();
  }, [fetchSavedSearchesCount]);

  useEffect(() => {
    fetchStockIssuesCount();
  }, [fetchStockIssuesCount]);

  const onToggleEditMode = () => {
    setIsEditMode((e) => !e);
  };

  const handleChangeFilters = (newFilters: FiltersParamsType) => {
    changeFilters({
      ...newFilters,
      page: 1, // Reset to the first page when changing filters
    });
  };

  const handleChangeSavedSearch = useUpsellFlow({
    title: t('filter.upsell.search'),
    featureKey: FEATURE_SEARCH_FILTER,
    trackingSource: ORDERS_SAVED_SEARCH_FILTER,
    includeCallbackResult: true,
    callback: (
      upsellResult: UpsellResultEnum,
      newFilters: FiltersParamsType,
    ) => {
      changeFilters({
        ...newFilters,
        page: 1, // Reset to the first page when changing filters
      });
      fetchSavedSearchesCount();
      trackingOrdersSearchFilterClick(upsellResult, 'saved_searches');
    },
    onCloseModal: (upsellResult) => {
      if (upsellResult === UpsellResultEnum.DECLINED) {
        trackingOrdersSearchFilterClick(upsellResult, 'saved_searches');
      }
    },
  });

  const handleFilterStockIssuesOrders = useUpsellFlow({
    title: t('filter.upsell.search'),
    featureKey: FEATURE_SEARCH_FILTER,
    trackingSource: ORDERS_STOCK_ISSUES_FILTER,
    includeCallbackResult: true,
    callback: (
      upsellResult: UpsellResultEnum,
      newFilters: FiltersParamsType,
    ) => {
      getOrdersList(newFilters);
      trackingOrdersSearchFilterClick(upsellResult, 'stock_issues');
    },
    onCloseModal: (upsellResult) => {
      if (upsellResult === UpsellResultEnum.DECLINED) {
        trackingOrdersSearchFilterClick(upsellResult, 'stock_issues');
      }
    },
  });

  // Upsell flow for orders auto cancel feature
  const handleOrdersAutoCancelClick = useOrdersAutoCancelUpsell({
    callback: () => {
      trackingOrderListAutoCancelButtonClick(nuvemPagoIsActive ?? false);
      goTo('/orders/auto-cancel');
    },
    nuvemPagoIsActive: nuvemPagoIsActive ?? false,
  });

  const isEmptyList = !isLoading && ordersIds.length === 0;

  const editModeAction = !isEmptyList
    ? {
        onClick: onToggleEditMode,
        children: isEditMode ? t('editMode.cancel') : t('editMode.edit'),
      }
    : undefined;

  const canExportOrdersCsv = useHasPermission('export_orders_csv');

  const exportOrdersAction: ActionProp = {
    children: t('exportOrders'),
    onClick: () => {
      const urlSearchParams = new URLSearchParams(search);
      urlSearchParams.delete('minUnits');
      urlSearchParams.delete('maxUnits');
      for (const [key, _] of urlSearchParams.entries()) {
        if (key.startsWith(METAFIELD_FILTER_PREFIX)) {
          urlSearchParams.delete(key);
        }
      }
      const parsedQueryParams = urlSearchParams.toString();
      goTo(`/orders/export?${parsedQueryParams}`);
      trackingOrderListExportClick();
    },
    icon: DownloadIcon,
  };
  const addDraftOrderAction: ActionProp = {
    children: t('draftOrders.addDraftOrder'),
    onClick: () => {
      trackingOrderListCreateDraftOrderClick();
      goTo('/draft-orders/new');
    },
    icon: PlusCircleIcon,
    appearance: 'primary',
  };

  const ordersAutoCancelAction = {
    children: t('autoCancel.title'),
    onClick: () => {
      handleOrdersAutoCancelClick();
    },
    icon: CogIcon,
    name: 'autocancel',
  } as ActionProp;

  const actions: ActionProp[] = canExportOrdersCsv
    ? [exportOrdersAction, addDraftOrderAction]
    : [addDraftOrderAction];

  const applications: ActionProp[] = getAppsLinks('orders', 'default')
    .filter((app) => {
      if (!isNuvemEnvioLink(app.url)) return true;
      return hasPrintShippingLabelPermission;
    })
    .map((app) => ({
      children: app.text,
      onClick: () => window.open(app.url, '_blank'),
    }));

  const extraOptionsActions: ActionProp[] = [];

  if (isOrdersAutoCancelEnabledForUpsell) {
    if (applications.length === 0 || isMobile || hasAutoCancelPromotionTag) {
      actions.unshift({
        ...ordersAutoCancelAction,
      });
    } else {
      extraOptionsActions.push(ordersAutoCancelAction);
    }
  }

  const renderNudgeComponent = useCallback(() => {
    if (componentType === 'cta' && isCtaVisible) {
      return <CtaActivateNuvemPago />;
    }
    if (componentType === 'callout' && isCalloutVisible) {
      return <CalloutCardActivateNuvemPago />;
    }
    return null;
  }, [componentType, isCtaVisible, isCalloutVisible]);

  if (!hasOrder && !isLoadingHasOrders && !isLoading) {
    return <OrderListEmpty />;
  }

  const titleDetail =
    totalOpenOrders > 0
      ? `${t('subtitleOpenOrders', { count: totalOpenOrders })}`
      : '';

  return (
    <IonPageStratus
      width={hasFullWidthOrdersListPageTag ? 'large' : 'medium'}
      headerTop={
        selectedRowsId.length === 0 && (
          <HeaderOrderMobile
            actions={actions}
            mainAction={editModeAction}
            applications={applications}
          />
        )
      }
      headerContent={
        <HeaderContent
          title={t('title')}
          titleDetail={titleDetail}
          actions={<ActionsDesktop actions={actions} renderActionsAsButtons />}
          applications={
            <ExtraOptionsHeaderDesktop
              actions={extraOptionsActions}
              applications={applications}
              ordersIds={selectedRowsId}
            />
          }
        />
      }
      headerFixed
    >
      <ErrorMetafieldAlert status={statusSuccess} />
      <AlertOrderMetafields show={showAlertMetafields} />
      {renderNudgeComponent()}

      {!isLoadingHasOrders && <NuvemEnvioWelcomeCreditCommunication />}

      <ChargebackAlert />

      <EmailValidationAlert />

      <StockIssuesAlert
        show={hasStockIssues}
        handleFilterStockIssuesOrders={handleFilterStockIssuesOrders}
      />

      <Box display="flex" flexDirection="column" gap={isDesktop ? 'none' : '4'}>
        <OrderListFilter
          showModal={showModal}
          filters={filters}
          ordersCount={ordersCount}
          isLoading={isLoading || isLoadingHasOrders}
          onChange={handleChangeFilters}
          onOpenModal={onOpenModal}
          onCloseModal={onCloseModal}
          handleChangeSavedSearch={handleChangeSavedSearch}
        />
        <OrderListResults
          hasOrder={hasOrder}
          isLoadingHasOrders={isLoadingHasOrders}
          isEditMode={isEditMode}
          onToggleEditMode={onToggleEditMode}
          onFilterLinkClick={() => onOpenModal()}
        />
      </Box>
      {isOrdersAutoCancelEnabledForUpsell &&
        !isMobile &&
        hasAutoCancelPromotionTag &&
        storeCountry === Country.AR && <OrdersAutoCancelPromotionModal />}
    </IonPageStratus>
  );
}

export default OrderListPageWithProvider;
